import {
  APPOINTMENT_CONFLICT_ERROR_CODE,
  appointmentConflictSchema,
  appointmentSchema,
  appointmentWeekSchema,
  type Appointment,
  type AppointmentConflict,
  type AppointmentWeek,
  type CreateAppointmentInput,
  type SetAppointmentStatusInput,
  type UpdateAppointmentInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';
import { isApiRequestError } from '../api/errors.js';

/**
 * Every appointment call the app makes.
 *
 * Every path lives here at the top, in one place, so a single edit realigns the whole module if
 * the backend (task 14) lands on a different spelling.
 */

const APPOINTMENTS = '/appointments';

export interface AppointmentWeekParams {
  /** The Monday the grid opens on, `'YYYY-MM-DD'`. */
  weekStart: string;
  /** Omitted asks for every staff member's appointments that week. */
  staffEmail?: string;
}

/** A flat list of everything booked in one week. What the calendar grid reads. */
export async function getAppointmentWeek(
  client: ApiClient,
  params: AppointmentWeekParams,
): Promise<AppointmentWeek> {
  return await client.get(`${APPOINTMENTS}/week`, {
    schema: appointmentWeekSchema,
    query: {
      weekStart: params.weekStart,
      ...(params.staffEmail ? { staffEmail: params.staffEmail } : {}),
    },
  });
}

export async function createAppointment(
  client: ApiClient,
  input: CreateAppointmentInput,
): Promise<Appointment> {
  return await client.post(APPOINTMENTS, input, { schema: appointmentSchema });
}

/** Moving or re-purposing a booking. Only the keys that changed — see `use-appointments.ts`. */
export async function updateAppointment(
  client: ApiClient,
  appointmentId: string,
  patch: UpdateAppointmentInput,
): Promise<Appointment> {
  return await client.patch(`${APPOINTMENTS}/${appointmentId}`, patch, {
    schema: appointmentSchema,
  });
}

/** Cancelling or completing a slot. */
export async function setAppointmentStatus(
  client: ApiClient,
  appointmentId: string,
  input: SetAppointmentStatusInput,
): Promise<Appointment> {
  return await client.post(`${APPOINTMENTS}/${appointmentId}/status`, input, {
    schema: appointmentSchema,
  });
}

/**
 * Reads a double booking out of a failed create or reschedule, the way `duplicatePhoneOf` reads
 * a duplicate customer out of a failed create.
 *
 * `null` for anything that is not this exact refusal — a non-API error, a non-409, a 409 with a
 * different code, or a 409 whose `details` do not parse as a conflict — so a caller can tell
 * "the server said this slot is taken" apart from every other way a request can fail.
 */
export function appointmentConflictOf(error: unknown): AppointmentConflict | null {
  if (!isApiRequestError(error) || error.status !== 409) return null;
  if (error.code !== APPOINTMENT_CONFLICT_ERROR_CODE) return null;
  const parsed = appointmentConflictSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}
